import React, { useState, Fragment } from 'react';
import type { ChangeEvent } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import Field from '@atlaskit/form/field';
import { ErrorMessage } from '@atlaskit/form/error-message';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Textfield from '@atlaskit/textfield/text-field';

const MAX_TITLE_NAME_LENGTH = 60;

const i18n = defineMessages({
	whiteboardTitle: {
		id: 'version-history.whiteboard-title',
		defaultMessage: 'Whiteboard title',
		description: 'Label for the field to set the title of the whiteboard',
	},
	titleLengthError: {
		id: 'version-history.title.length.error',
		defaultMessage:
			'{maxLength, plural, one {Title has a maximum of {maxLength} character} other {Title has a maximum of {maxLength} characters}}',
		description: 'error to be shown once the title exceeds the maximum characters allowed',
	},
	databaseTitle: {
		id: 'version-history.database-title',
		defaultMessage: 'Database title',
		description: 'Label for the field to set the title of the database',
	},
	slideTitle: {
		id: 'version-history.slide-title',
		defaultMessage: 'Slide deck title',
		description: 'Label for the field to set the title of the slide deck',
	},
});

const titleUpdateMessaging: { [key: string]: MessageDescriptor } = {
	whiteboard: i18n.whiteboardTitle,
	database: i18n.databaseTitle,
	slide: i18n.slideTitle,
};

type VersionTitleUpdateProps = {
	title: string;
	setTitle: React.Dispatch<React.SetStateAction<string>>;
	contentType: string;
};

const labelComponentStyle = xcss({
	marginBottom: 'space.100',
	color: 'color.text.subtlest',
});

const LabelComponent = ({ contentType }: { contentType: string }) => (
	<Box xcss={labelComponentStyle}>
		<FormattedMessage {...titleUpdateMessaging[contentType]} />
		<RequiredAsterisk />
	</Box>
);

export const VersionTitleUpdate: React.FC<VersionTitleUpdateProps> = ({
	title,
	setTitle,
	contentType,
}) => {
	const { formatMessage } = useIntl();
	const [hasError, setHasError] = useState<boolean>(false);

	const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		if (value.length <= MAX_TITLE_NAME_LENGTH) {
			setTitle(value);
			setHasError(false);
		} else {
			setHasError(true);
		}
	};

	return (
		<Fragment>
			<Field label={<LabelComponent contentType={contentType} />} name="version-title">
				{({ fieldProps }: any) => (
					<Textfield
						{...fieldProps}
						value={title}
						onChange={handleTitleChange}
						maxLength={MAX_TITLE_NAME_LENGTH + 1}
						autoComplete="off"
						isInvalid={hasError}
						testId="version-title-input"
					/>
				)}
			</Field>
			{hasError && (
				<ErrorMessage>
					{formatMessage(i18n.titleLengthError, {
						maxLength: MAX_TITLE_NAME_LENGTH,
					})}
				</ErrorMessage>
			)}
		</Fragment>
	);
};
